import { useState } from 'react';
import { Field, Form } from 'react-final-form';
import CurrencyFormat from 'react-number-format';
import { Col, Row, Table } from 'reactstrap';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { appendLocale, getBase64 } from '../../../utils/helper';

import { useEffect } from 'react';
import { MdOutlineSimCardDownload } from 'react-icons/md';
import Icon from '../../../assets/icons/Icon';
import CustomCheckBox from '../../../components/form/CustomCheckBox';
import CustomInputField from '../../../components/form/CustomInputField';
import CustomSelect from '../../../components/form/CustomSelect';
import DatePickerFinal from '../../../components/form/DatePickerFinal';
import Modal from '../../../components/modal/Modal';
import { useDict } from '../../../contexts/LanguageContext';
import { useStatusModalContext } from '../../../contexts/StatusModalContext';
import RiskDisclosureStatementEngPDF from '../../../documents/risk_disclosure_statement_EN.pdf';
import RiskDisclosureStatementThaiPDF from '../../../documents/risk_disclosure_statement_TH.pdf';
import enErrors from '../../../languages/enErrors.json';
import thErrors from '../../../languages/thErrors.json';
import { KnowledgeTestQuestionOptions } from '../../../utils/constants';
import {
    normalizeCurrency,
    parseAmountFloat,
    parseAmountStr,
} from '../../../utils/helper';
import { useCopyToClipboard } from '../../../utils/hooks';
import { getDownloadInvestmentMemo } from '../../investmentNote/src/investmentNoteApis';
import { getInvestModalOptions, postSubmitInvest } from '../src/fundApis';
import InvestProofInput from './InvestProofInput';


const InvestModal = (props) => {
    const { handleClose, investmentNote, isOpen } = props;

    const dict = useDict();

    const language = localStorage.getItem('language') === 'en' ? 'en' : 'th';

    const [isCopied, handleCopy] = useCopyToClipboard();
    const { openStatusModal } = useStatusModalContext();

    const [bankOptions, setBankOptions] = useState([]);
    const [bankAccTypeOptions, setBankAccTypeOptions] = useState([]);
    const [bankTransferTypeOptions, setBankTransferTypeOptions] = useState([]);
    const [knowledgeTestQuestions, setKnowledgeTestQuestions] = useState([]);
    const [investBankAccountOptions, setInvestBankAccountOptions] = useState([]);
    const [trusteeBankAccData, setTrusteeBankAccData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckedRetailInvestor, setIsCheckedRetailInvestor] = useState(false);
    const [isCheckedRDS, setIsCheckedRDS] = useState(false);
    const [isCheckedKnowledgeTest, setIsCheckedKnowledgeTest] = useState(false);

    useEffect(() => {
        if (isOpen) {
            getInvestModalOptions(handleGetBankTransferData);
        }
    }, [isOpen]);

    const handleGetBankTransferData = (response) => {
        const { data: responseData } = response.data;
        const bankAccounts = responseData.bank_accounts.map((account) => {
            return {
                value: account.id,
                label: account.bank[appendLocale('name')] + ' [Account No: ' + account.account_no + ' - ' + account.account_owner_name + ']',
            };
        });

        const transferTypes = responseData.transfer_types.map((types) => {
            return {
                value: types.id,
                label: types[appendLocale('name')],
            }
        });

        const knowledgeTestQuestions = responseData.knowledge_questions.map((question) => {
            return {
                value: question.expected_option == KnowledgeTestQuestionOptions.YES ? dict.yes : dict.no,
                label: question[appendLocale('question')],
            }
        });

        setBankOptions(responseData.bank_options);
        setBankAccTypeOptions(responseData.bank_acc_type_options);
        setBankTransferTypeOptions(transferTypes);
        setKnowledgeTestQuestions(knowledgeTestQuestions);
        setInvestBankAccountOptions(bankAccounts);
        setTrusteeBankAccData(responseData.trustee_bank_acc_data);
    };

    const handlePostSubmitInvest = (response) => {
        handleClose();
        openStatusModal({
            message:
                dict.investmentSuccessfulMessage,
            title: dict.invest,
            isSuccess: true,
            refresh: true,
        });
    };

    const handlePostSubmitInvestError = (error) => {
        if (enErrors.hasOwnProperty(error.response.data.error)) {
            openStatusModal({
                message: (language === 'en' ? enErrors[error.response.data.error] : thErrors[error.response.data.error]),
                title: dict.invest,
                isSuccess: false,
                refresh: false,
            });
        } else {
            openStatusModal({
                message: dict.investmentFailedMessage,
                title: dict.invest,
                isSuccess: false,
                refresh: true,
            });
        }
    };

    const submitInvest = async (formData) => {
        setIsLoading(true);

        await postSubmitInvest(
            formData,
            handlePostSubmitInvest,
            handlePostSubmitInvestError
        );

        setIsLoading(false);
    };

    const handleSubmitInvest = async (value) => {
        const formData = new FormData();
        const amount = parseAmountStr(value.amount.substring(2));
        const base64String = await getBase64(value.files);
        // const payeeDateTime = formatDateTime(value.date_time);

        formData.append('amount', amount);
        formData.append('bank_account_id', value.acc_bank_id);
        formData.append('transfer_type_id', value.transfer_type_id);
        formData.append('invest_base64_string', base64String);
        formData.append('invest_file_size', value.files.size);
        formData.append('invest_file_name', value.files.name);
        formData.append('invest_file_type', value.files.type);
        formData.append('investment_note_id', value.investment_note_id);
        // formData.append('payer_account_bank_id', value.acc_bank_id);
        // formData.append('payee_date_time', payeeDateTime);
        // formData.append('bank_transfer_type_id', value.transfer_type_id);
        // formData.append('document', value.files);
        // formData.append('bank_id', value.bank_id);
        // formData.append('account_type_id', value.bank_type_id);
        // formData.append('account_bank_number', value.account_no);
        // formData.append('account_name', value.account_name);
        // formData.append('send_email', value.send_email ? 'true' : 'false');
        // formData.append('country_id', Country.Malaysia);

        submitInvest(formData);
    };

    const investValidate = (values) => {
        const errors = {};
        if (!values.date_time) {
            errors.date_time = dict.dateSelectEmpty;
        }

        if (!values.transfer_type_id) {
            errors.transfer_type_id = dict.transferTypeSelectEmpty;
        }

        if (!values.acc_bank_id) {
            errors.acc_bank_id = dict.bankAccountSelectEmpty;
        } else if (values.acc_bank_id === '0') {
            if (!values.bank_id) {
                errors.bank_id = dict.bankSelectEmpty;
            }
            if (!values.account_no) {
                errors.account_no = dict.accountNumberSelectEmpty;
            }
            if (!values.account_name) {
                errors.account_name = dict.accountNameSelectEmpty;
            }
            if (!values.bank_type_id) {
                errors.bank_type_id = dict.accountTypeSelectEmpty;
            }
        }

        if (values.amount) {
            const floatAmount = parseAmountFloat(values.amount.substr(2));
            if (floatAmount < 10000) {
                errors.amount = dict.invalidInvestAmount;
            }

            if (floatAmount > (investmentNote.remaining_amount)) {
                errors.amount = dict.invalidInvestAmount2;
            }
        } else {
            errors.amount = dict.invalidInvestAmount;
        }

        if (!values.files) {
            errors.files = dict.paymentProofFileEmpty;
        }

        if (values.files) {
            if (values.files.size > 4194304) {
                errors.files = dict.fileInvalid;
            }
        }

        return errors;
    };

    const onDownloadInvestmentMemo = (investmentNoteId) => {
        const payload = {
            investment_note_id: investmentNoteId
        };

        getDownloadInvestmentMemo(payload);
    }

    const openPDFPreview = () => {
        window.open(
            language === 'th' ? RiskDisclosureStatementThaiPDF : RiskDisclosureStatementEngPDF
            , '_blank'
        );
    };

    return (
        <Modal
            toggle={handleClose}
            isOpen={isOpen}
            testID="investModal"
            submitBtnProps={{
                form: 'investForm',
                disabled: isLoading || !isCheckedRetailInvestor || !isCheckedRDS || !isCheckedKnowledgeTest,
            }}
            onCancel={handleClose}
            headerType="review-2"
            title={dict.bankTransfer}
            submitShown
            cancelText={dict.cancel}
            submitText={dict.submit}
            size="md"
        >
            <div className="modal__body" data-cy="modal__content">
                <Row className="mb-3 text-left g-0">
                    <p className="text-danger">
                        <strong>{dict.importantNote}:</strong>
                    </p>
                    <p className="text-danger text-height-1 m-0">
                        {dict.bankTransferText}
                    </p>
                </Row>
                <Row className="mb-3 g-0">
                    <Col
                        md={6}
                        sm={12}
                        xs={6}
                        className="text-bold text-left"
                    >
                        {dict.investmentNote}:
                    </Col>
                    <Col
                        md={6}
                        sm={12}
                        xs={6}
                        className="text-left"
                    >
                        {investmentNote?.name}
                        {
                            investmentNote?.has_investment_memo && (
                                <div
                                    className='icon-wrapper ml-1'
                                    onClick={() => onDownloadInvestmentMemo(investmentNote?.id)}
                                >
                                    <MdOutlineSimCardDownload size={18} color='#f29525' />
                                </div>
                            )
                        }
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col
                        md={6}
                        sm={12}
                        xs={6}
                        className="text-bold text-left"
                    >
                        {dict.availableAmount}:
                    </Col>
                    <Col
                        md={6}
                        sm={12}
                        xs={6}
                        className="text-left p-0"
                        data-private
                    >
                        <CurrencyFormat
                            value={parseFloat(
                                investmentNote?.remaining_amount ?? 0
                            )}
                            displayType="text"
                            thousandSeparator
                            prefix="฿ "
                        />
                    </Col>
                </Row>
                <Row className='g-0'>
                    <Col>
                        <p className="font-weight-bold mb-2 text-left">
                            {dict.details}:
                        </p>
                        <Form
                            onSubmit={(values) => handleSubmitInvest(values)}
                            validate={investValidate}
                            initialValues={{
                                investment_note_id: investmentNote?.id,
                                date_time: '',
                                transfer_type_id: '',
                                acc_bank_id: '',
                                bank_id: '',
                                account_no: '',
                                account_name: '',
                                bank_type_id: '',
                                amount: '',
                                // send_email: false,
                            }}
                        >
                            {({ handleSubmit, values }) => (
                                <form
                                    id="investForm"
                                    className="form form-invest form-custom"
                                    onSubmit={handleSubmit}
                                    style={{ textAlign: 'left' }}
                                >
                                    <Field name="acc_bank_id">
                                        {({ input, meta }) => (
                                            <CustomSelect
                                                input={input}
                                                meta={meta}
                                                options={
                                                    investBankAccountOptions ||
                                                    []
                                                }
                                                placeholder={
                                                    dict.selectBankAccount
                                                }
                                                data-cy="invest__bankAccount"
                                                className="mb-3"
                                            />
                                        )}
                                    </Field>

                                    {values.acc_bank_id === '0' && (
                                        <>
                                            <Field
                                                name="bank_id"
                                                className="theme-light react-select__control react-select"
                                                style={{ color: '#646777' }}
                                            >
                                                {({ input, meta }) => (
                                                    <div className="form__form-group">
                                                        <div
                                                            className="form__form-group-field"
                                                            style={{
                                                                marginTop: '5%',
                                                            }}
                                                        >
                                                            <CustomSelect
                                                                input={input}
                                                                meta={meta}
                                                                options={
                                                                    bankOptions ||
                                                                    []
                                                                }
                                                                placeholder={
                                                                    dict.selectBank
                                                                }
                                                                data-cy="invest__bank"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </Field>

                                            <Field name="account_no">
                                                {({ input, meta }) => (
                                                    <div className="form__form-group">
                                                        <div
                                                            className="form__form-group-field"
                                                            data-private
                                                        >
                                                            <CustomInputField
                                                                type="number"
                                                                input={input}
                                                                meta={meta}
                                                                placeholder={
                                                                    dict.accountNumberText +
                                                                    ' *'
                                                                }
                                                                data-cy="invest__accountNumber"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </Field>

                                            <Field name="account_name">
                                                {({ input, meta }) => (
                                                    <div className="form__form-group">
                                                        <div
                                                            className="form__form-group-field"
                                                            style={{
                                                                marginTop: '5%',
                                                            }}
                                                            data-private
                                                        >
                                                            <CustomInputField
                                                                type="text"
                                                                input={input}
                                                                meta={meta}
                                                                placeholder={
                                                                    dict.accountNameText +
                                                                    ' *'
                                                                }
                                                                data-cy="invest__accountName"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </Field>
                                            <Field name="bank_type_id">
                                                {({ input, meta }) => (
                                                    <div className="form__form-group">
                                                        <div className="form__form-group-field">
                                                            <CustomSelect
                                                                input={input}
                                                                meta={meta}
                                                                options={
                                                                    bankAccTypeOptions ||
                                                                    []
                                                                }
                                                                placeholder={
                                                                    dict.accountType
                                                                }
                                                                data-cy="invest__accountType"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </Field>
                                        </>
                                    )}

                                    <Field name="transfer_type_id">
                                        {({ input, meta }) => (
                                            <CustomSelect
                                                input={input}
                                                meta={meta}
                                                options={
                                                    bankTransferTypeOptions ||
                                                    []
                                                }
                                                placeholder={dict.transferType}
                                                data-cy="invest__transferType"
                                                className="mb-3"
                                            />
                                        )}
                                    </Field>

                                    <Field
                                        name="amount"
                                        parse={normalizeCurrency}
                                    >
                                        {({ input, meta }) => (
                                            <CustomInputField
                                                type="text"
                                                input={input}
                                                meta={meta}
                                                placeholder={dict.investMinAmount}
                                                data-cy="invest__amount"
                                                className="mb-3"
                                                mask={createNumberMask({
                                                    allowDecimal: true,
                                                    prefix: '฿ ',
                                                })}
                                            />
                                        )}
                                    </Field>

                                    <p className="font-weight-bold mb-3">
                                        {dict.transferToCapBay}
                                    </p>
                                    <Row
                                        className="border rounded-lg shadow-sm px-4 py-3 mb-4 g-0"
                                    >
                                        <Col xs={4}>
                                            <p className="font-weight-bold">
                                                {dict.bankName}:
                                            </p>
                                        </Col>
                                        <Col>
                                            <p>
                                                {trusteeBankAccData?.bank_name}
                                                Kiatnakin Phatra Bank
                                            </p>
                                        </Col>
                                        <div className="w-100" />
                                        <Col xs={4}>
                                            <p className="font-weight-bold text-capitalize">
                                                {dict.accountNameText}:
                                            </p>
                                        </Col>
                                        <Col>
                                            <p>
                                                {trusteeBankAccData?.account_owner_name}
                                                Investment Account
                                            </p>
                                        </Col>
                                        <div className="w-100" />
                                        <Col xs={4}>
                                            <p className="font-weight-bold">
                                                {dict.accountNo}:
                                            </p>
                                        </Col>
                                        <Col>
                                            <button
                                                type="button"
                                                style={{
                                                    outline: 'none',
                                                    border: 'none',
                                                    background: 'white',
                                                }}
                                                className="bg-white p-0"
                                                onClick={() =>
                                                    handleCopy(
                                                        trusteeBankAccData?.account_no ||
                                                        'N/A'
                                                    )
                                                }
                                            >
                                                <span
                                                    style={{ color: '#004FAD' }}
                                                >
                                                    {trusteeBankAccData?.account_no}{' '}
                                                    123412341234{' '}
                                                    <Icon icon="Copy" inline />
                                                </span>
                                                {isCopied && (
                                                    <span> {dict.copied}</span>
                                                )}
                                            </button>
                                        </Col>
                                    </Row>

                                    <Field name="date_time">
                                        {({ input, meta }) => (
                                            <DatePickerFinal
                                                input={input}
                                                meta={meta}
                                                data-cy="invest__date"
                                                className="mb-4"
                                                wrapperClassName="w-100"
                                            />
                                        )}
                                    </Field>

                                    <p className="font-weight-bold mb-3">
                                        {dict.uploadProofOfPayment}:
                                    </p>
                                    <Field name="files" type="file">
                                        {({ input, meta }) => (
                                            <InvestProofInput
                                                input={input}
                                                meta={meta}
                                                data-cy="invest__file"
                                                className="mb-3"
                                            />
                                        )}
                                    </Field>

                                    <Row className="g-0">
                                        <Icon
                                            icon="WarningCircle"
                                            color="red"
                                            width={25}
                                            className="ml-auto mr-3"
                                        />
                                        <div className="mr-auto">
                                            <p>
                                                {dict.proofOfPaymentSubtitle}:
                                            </p>
                                            <ul className="ml-3">
                                                <li>
                                                    {dict.proofOfPaymentText1}
                                                </li>
                                                <li>
                                                    {dict.proofOfPaymentText2}
                                                </li>
                                                <li>
                                                    {dict.proofOfPaymentText3}
                                                </li>
                                            </ul>
                                        </div>
                                    </Row>
                                    <Row className="g-0">
                                        <div className="mr-auto">
                                            <p>
                                                {dict.retailInvestorNote}:
                                            </p>
                                            <ul className="ml-3">
                                                <li>
                                                    {dict.retailInvestorNoteList1}
                                                </li>
                                                <li>
                                                    {dict.retailInvestorNoteList2}
                                                </li>
                                            </ul>
                                        </div>
                                    </Row>
                                    <Row className="g-0 mt-2">
                                        <div className="mr-auto">
                                            <h5
                                                className="bold-text"
                                                style={{ fontSize: '12px' }}
                                            >
                                                {dict.tncSubtitle1}
                                            </h5>
                                            <p className="underline-text" style={{ textAlign: 'justify' }}>
                                                {dict.tncSubtitle1Text1}
                                            </p>
                                            <p style={{ textAlign: 'justify' }}>
                                                {dict.tncSubtitle1Text2}
                                            </p>
                                            <p style={{ textAlign: 'justify' }}>
                                                {dict.riskDisclosureStatementText1}
                                                <a className="text-blue cursor-pointer" onClick={openPDFPreview}>
                                                    {dict.riskDisclosureStatementText2}
                                                </a>
                                                {dict.riskDisclosureStatementText3}
                                            </p>
                                            <p style={{ textAlign: 'justify' }}>
                                                {dict.tncSubtitle1Text4}
                                            </p>
                                            <ul className="ml-3">
                                                <li>{dict.tncSubtitle1Text4List1}</li>
                                                <li>{dict.tncSubtitle1Text4List2}</li>
                                                <li>{dict.tncSubtitle1Text4List3}</li>
                                            </ul>
                                            <p style={{ textAlign: 'justify' }}>
                                                {dict.tncSubtitle1Text5}
                                            </p>
                                            <p style={{ textAlign: 'justify' }}>
                                                {dict.tncSubtitle1Text6}
                                            </p>
                                        </div>
                                    </Row>

                                    <Row className="g-0 mt-2 mb-4">
                                        <div className="mr-auto">
                                            <h5
                                                className="bold-text"
                                                style={{ fontSize: '12px' }}
                                            >
                                                {dict.investorKnowledgeTest}
                                            </h5>

                                            <p className="mb-2">{dict.investorKnowledgeTestText}</p>

                                            <Table responsive className="invest-now--knowledge-test-table table-striped">
                                                <thead>
                                                    <tr className="text-center invest-now--knowledge-test-table-header">
                                                        <th className="invest-now--knowledge-test-table-label">{dict.question}</th>
                                                        <th className="invest-now--knowledge-test-table-value">{dict.answer}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {knowledgeTestQuestions ? (
                                                        knowledgeTestQuestions.map((data, index) => (
                                                            <tr key={index}>
                                                                <td>
                                                                    <div className="align-text-left">
                                                                        {data.label}
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div className="text-center">
                                                                        {data.value}
                                                                    </div>
                                                                </td>

                                                            </tr>
                                                        ))
                                                    ) : null}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Row>

                                    <Field name="retail_investor_declaration" type="checkbox">
                                        {({ input, meta }) => (
                                            <CustomCheckBox
                                                input={input}
                                                meta={meta}
                                                label={dict.retailInvestorCheckboxText}
                                                className="custom"
                                                setIsChecked={setIsCheckedRetailInvestor}
                                            />
                                        )}
                                    </Field>
                                    <Field name="risk_disclosure_statement_declaration" type="checkbox">
                                        {({ input, meta }) => (
                                            <CustomCheckBox
                                                input={input}
                                                meta={meta}
                                                label={dict.riskDisclosureStatementCheckboxText}
                                                className="custom"
                                                setIsChecked={setIsCheckedRDS}
                                            />
                                        )}
                                    </Field>
                                    <Field name="investor_knowledge_test_declaration" type="checkbox">
                                        {({ input, meta }) => (
                                            <CustomCheckBox
                                                input={input}
                                                meta={meta}
                                                label={dict.investorKnowledgeTestCheckboxText}
                                                className="custom"
                                                setIsChecked={setIsCheckedKnowledgeTest}
                                            />
                                        )}
                                    </Field>
                                </form>
                            )}
                        </Form>
                    </Col>
                </Row>
            </div>
        </Modal>
    );
};

export default InvestModal;
